import { observer, Observer } from "mobx-react";
import { Answer } from "models/Answer";
import { AnswerTypeNames } from "models/AnswerType";
import Question from "models/Question";
import React from "react";
import { ThemeColors } from "Theme/AppTheme";
import hexToRGB from "utils/hexToRGB";
import { Divider, Grid } from "@mui/material";
import { buildQuestionVisibilityMap } from "utils/questionDependencyRendering";
import ConvoNote from "./ConvoNote";
import ConvoScaledResponseAnswer from "./ConvoScaledResponseAnswer";
import ConvoStarRatingAnswer from "./ConvoStarRatingAnswer";
import ConvoTOFIResponseAnswer from "./ConvoTOFIResponseAnswer";
import { ConvoModuleUIModel } from "./ConvoModuleUIModel";
import { Tag } from "models/Tag";

export interface ConvoQuestion extends Question {
    conversationModuleId: string;
}

interface OwnProps {
    convoModuleId?: string;
    isEditable?: boolean;
    workflowLevel?: number;
    showClipUi?: boolean;
    uiModel?: ConvoModuleUIModel;
    sortedQuestions: ConvoQuestion[];
    isQuestionAutofilled: (questionId: string) => boolean;
    getAnswerForQuestion: (
        question: Question,
        conversationModuleId: string | undefined,
    ) => Answer | undefined;
    setAnswerForQuestion: (
        question: Question,
        tags: Tag[],
        answer: Answer | undefined,
        conversationModuleId?: string,
    ) => Answer;
    setFillInAnswerForQuestion: (
        question: Question,
        fillInAnswer: string,
        answer: Answer | undefined,
        conversationModuleId?: string,
    ) => Answer;
}

type Props = OwnProps;

export function buildConvoQuestionVisibilityMap(
    questions: ConvoQuestion[],
    answers: Answer[] | undefined,
): Map<string, boolean> {
    return buildQuestionVisibilityMap({
        questions,
        answers,
        getModuleInstanceIdForQuestion: (question) =>
            question.conversationModuleId,
        getModuleInstanceIdForAnswer: (answer) => answer.conversationModuleId,
    });
}

const ConvoQuestionAnswerList = observer((props: Props) => {
    const questionsWithDependencies = props.sortedQuestions.filter(
        (r) => r.dependencies?.conditions.length,
    );

    const renderQuestion = (questionValue: ConvoQuestion): React.ReactNode => {
        let questionComponent: React.ReactNode = <></>;

        if (questionValue.isActive) {
            const leftBorder =
                questionValue.parentId &&
                questionsWithDependencies?.includes(questionValue)
                    ? hexToRGB(ThemeColors.purple, 1)
                    : questionsWithDependencies?.includes(questionValue)
                    ? hexToRGB(ThemeColors.red, 0.75)
                    : questionValue.parentId
                    ? hexToRGB(ThemeColors.blue, 0.5)
                    : undefined;

            if (
                questionValue.answerType.answerTypeName ===
                    AnswerTypeNames.ScaledResponse ||
                questionValue.answerType.answerTypeName ===
                    AnswerTypeNames.ScaledBooleanResponse ||
                questionValue.answerType.answerTypeName ===
                    AnswerTypeNames.QuestionGrouping
            ) {
                questionComponent = (
                    <ConvoScaledResponseAnswer
                        moduleUIModel={props.uiModel}
                        question={questionValue}
                        leftBorderColor={leftBorder}
                        key={questionValue.id}
                        isDisabled={!props.isEditable}
                        isAutofilled={props.isQuestionAutofilled(
                            questionValue.id,
                        )}
                        getAnswerForQuestion={props.getAnswerForQuestion}
                        setAnswerForQuestion={props.setAnswerForQuestion}
                        showClips={props.showClipUi}
                    />
                );
            } else if (
                questionValue.answerType.answerTypeName ===
                    AnswerTypeNames.TagResponse ||
                questionValue.answerType.answerTypeName ===
                    AnswerTypeNames.ScoredTagResponse ||
                questionValue.answerType.answerTypeName ===
                    AnswerTypeNames.TextResponse ||
                questionValue.answerType.answerTypeName ===
                    AnswerTypeNames.DateResponse
            ) {
                questionComponent = (
                    <ConvoTOFIResponseAnswer
                        key={questionValue.id}
                        leftBorderColor={leftBorder}
                        moduleUIModel={props.uiModel}
                        question={questionValue}
                        isDisabled={!props.isEditable}
                        isAutofilled={props.isQuestionAutofilled(
                            questionValue.id,
                        )}
                        setAnswerForQuestion={props.setAnswerForQuestion}
                        getAnswerForQuestion={props.getAnswerForQuestion}
                        setFillInAnswerForQuestion={
                            props.setFillInAnswerForQuestion
                        }
                        showClips={props.showClipUi}
                    />
                );
            } else if (
                questionValue.answerType.answerTypeName ===
                AnswerTypeNames.StarRating
            ) {
                questionComponent = (
                    <ConvoStarRatingAnswer
                        key={questionValue.id}
                        moduleUIModel={props.uiModel}
                        question={questionValue}
                        leftBorderColor={leftBorder}
                        isDisabled={!props.isEditable}
                        isAutofilled={props.isQuestionAutofilled(
                            questionValue.id,
                        )}
                        getAnswerForQuestion={props.getAnswerForQuestion}
                        setAnswerForQuestion={props.setAnswerForQuestion}
                    />
                );
            }
            return (
                <React.Fragment key={questionValue.id}>
                    {questionComponent}
                    <Divider />
                </React.Fragment>
            );
        } else {
            return "";
        }
    };

    return (
        <Observer>
            {() => (
                <Grid container item xs={12} style={{ textWrap: "wrap" }}>
                    {!!props.convoModuleId ? (
                        <Grid item xs={12}>
                            {props.sortedQuestions?.map(renderQuestion)}
                        </Grid>
                    ) : (
                        <ConvoNote
                            columnDirection={"column"}
                            workflowLevel={props.workflowLevel}
                            isDisabled={!props.isEditable}
                        />
                    )}
                </Grid>
            )}
        </Observer>
    );
});

export default ConvoQuestionAnswerList;
